import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import './EventPhotos.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://live-event-photos.onrender.com/api';

function PublicGallery() {
  const { token } = useParams();
  const [attendee, setAttendee] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activePhotoId, setActivePhotoId] = useState(null);

  // Always use dark mode logo for gallery
  const logoUrl = 'https://storage.googleapis.com/msgsndr/oNgjqmOcyIwtcMEEfqUj/media/69051e7a306550caec6c2c16.webp';

  // Toggle overlay on mobile tap
  const handlePhotoTap = (photoId) => {
    setActivePhotoId(activePhotoId === photoId ? null : photoId);
  };

  // Save photo - uses iOS Share Sheet on iOS, download on Android/Desktop
  const handleDownload = async (photoUrl, photoName) => {
    console.log('📥 Save clicked:', photoUrl);
    
    try {
      // Detect iOS
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      
      if (isIOS && navigator.share) {
        // iOS: Use Web Share API to trigger native Share Sheet
        console.log('📱 iOS detected - using Share Sheet');
        
        // CRITICAL: Use backend proxy to fetch image (fixes CORS issues with B2)
        // B2 URLs don't have CORS headers, so direct fetch() fails
        // Backend proxy fetches from B2 and serves with proper CORS headers
        const proxyUrl = `${API_URL}/download-photo?url=${encodeURIComponent(photoUrl)}&filename=${encodeURIComponent(photoName || 'photo.jpg')}`;
        
        const response = await fetch(proxyUrl);
        if (!response.ok) {
          throw new Error(`Proxy fetch failed: ${response.status}`);
        }
        const blob = await response.blob();
        
        // Create a File object from the blob (required for sharing images)
        // IMPORTANT: Explicitly set type to 'image/jpeg' (matching working SharePhoto.js)
        const file = new File([blob], photoName || 'photo.jpg', { type: 'image/jpeg' });
        
        // Trigger native share sheet
        await navigator.share({
          title: `Photo from ${attendee?.event_name || 'EventPic'}`,
          text: 'Check out this photo!',
          files: [file]
        });
        
        console.log('✅ iOS: Share Sheet opened');
      } else {
        // Android/Desktop: Direct blob download
        console.log('💾 Using download method');
        
        // ALSO use proxy for Android (B2 CORS issue affects all browsers)
        const proxyUrl = `${API_URL}/download-photo?url=${encodeURIComponent(photoUrl)}&filename=${encodeURIComponent(photoName || 'photo.jpg')}`;
        
        const response = await fetch(proxyUrl);
        if (!response.ok) {
          throw new Error(`Proxy fetch failed: ${response.status}`);
        }
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = photoName || 'photo.jpg';
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
        console.log('✅ Download triggered');
      }
    } catch (error) {
      console.error('❌ Save failed:', error);
      // Fallback: open in new tab
      alert(`Could not save photo: ${error.message}. Opening in new tab...`);
      window.open(photoUrl, '_blank');
    }
  };

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const response = await axios.get(`${API_URL}/gallery/${token}`);
        setAttendee(response.data.attendee);
        setPhotos(response.data.photos);
      } catch (err) {
        console.error('Failed to load gallery:', err);
        setError(err.response?.data?.error || 'Failed to load gallery');
      } finally {
        setLoading(false);
      }
    };

    fetchGallery();
  }, [token]);

  if (loading) {
    return (
      <div className="loading-screen" style={{ backgroundColor: '#0f172a', color: '#fff' }}>
        <div className="loading-content">
          <div className="spinner"></div>
          <p>Loading your photos...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="events-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', backgroundColor: '#0f172a' }}>
        <div style={{ textAlign: 'center', maxWidth: '400px', padding: '20px' }}>
          <h2 style={{ color: '#EF4444', marginBottom: '16px' }}>❌ Gallery Not Found</h2>
          <p style={{ color: '#94a3b8' }}>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="events-container" style={{ backgroundColor: '#0f172a', minHeight: '100vh', color: '#fff' }}>
      <header className="events-header" style={{ 
        flexDirection: 'column', 
        alignItems: 'center', 
        gap: '16px', 
        padding: '24px',
        backgroundColor: '#1e293b',
        borderBottom: '1px solid #334155'
      }}>
        <img 
          src={logoUrl} 
          alt="EventPic Live" 
          style={{ 
            height: '50px',
            display: 'block',
            margin: '0 auto'
          }}
        />
        <div style={{ textAlign: 'center', width: '100%' }}>
          <h1 style={{ 
            margin: '0 0 8px 0', 
            fontSize: 'clamp(20px, 5vw, 28px)',
            color: '#fff'
          }}>Your Photo Gallery</h1>
          <p style={{ 
            margin: 0, 
            fontSize: 'clamp(14px, 4vw, 18px)', 
            color: '#94a3b8'
          }}>
            {attendee?.name} • {attendee?.eventName}
          </p>
        </div>
        <p style={{ 
          margin: '0 auto', 
          fontSize: 'clamp(14px, 3.5vw, 16px)', 
          color: '#94a3b8', 
          maxWidth: '600px', 
          textAlign: 'center',
          padding: '0 16px'
        }}>
          Thanks for using EventPic Live! Here are all the photos you appear in from this event.
        </p>
      </header>

      <div className="photos-content" style={{ padding: 'clamp(12px, 3vw, 20px)', backgroundColor: '#0f172a', marginLeft: 0, maxWidth: '100%' }}>
        {photos.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: '#94a3b8' }}>
            <p style={{ fontSize: '18px', marginBottom: '8px' }}>No photos found</p>
            <p>You don't appear in any photos from this event yet.</p>
          </div>
        ) : (
          <>
            <div style={{ textAlign: 'center', marginBottom: '24px', color: '#94a3b8' }}>
              <p style={{ fontSize: '16px', margin: 0 }}>
                {photos.length} {photos.length === 1 ? 'photo' : 'photos'} found
              </p>
            </div>
            <div className="photos-grid">
              {photos.map((photo) => {
                // Convert HEIC to JPEG for browser display
                let displayUrl = photo.url;
                // Only Cloudinary needs HEIC conversion - B2 photos are already converted at upload time
                if (displayUrl.includes('cloudinary.com') && (displayUrl.endsWith('.heic') || displayUrl.endsWith('.heif'))) {
                  displayUrl = displayUrl.replace('/upload/', '/upload/f_jpg/');
                }
                const imgUrl = `${displayUrl}${displayUrl.includes('?') ? '&' : '?'}v=${photo.uploadedAt}`;

                return (
                  <div key={photo.id} className="photo-card" style={{ position: 'relative' }}>
                    <div 
                      className={`photo-image-wrapper ${activePhotoId === photo.id ? 'show-overlay' : ''}`}
                      onClick={() => handlePhotoTap(photo.id)}
                      style={{ cursor: 'pointer' }}
                    >
                      <img src={imgUrl} alt={photo.originalName} />
                      <div className="photo-overlay">
                        <div className="photo-overlay-content">
                          <p className="overlay-timestamp">{new Date(photo.uploadedAt).toLocaleString()}</p>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownload(photo.url, photo.originalName);
                            }}
                            className="overlay-download-button"
                            style={{
                              display: 'inline-block',
                              padding: '10px 20px',
                              backgroundColor: '#10B981',
                              color: 'white',
                              borderRadius: '8px',
                              border: 'none',
                              fontWeight: 'bold',
                              marginTop: '12px',
                              cursor: 'pointer'
                            }}
                          >
                            Save Photo
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      
    </div>
  );
}

export default PublicGallery;
